import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingButtons from '@/pages/home/components/FloatingButtons';
import Accordion from '@/components/base/Accordion';
import ProgressiveImage from '@/components/base/ProgressiveImage';

const features = [
  { icon: 'ri-lock-2-line', title: 'Tam Güvenlik', desc: 'Yükünüz kendi araçta, kendi sürücümüzle taşınır. Başka yük ile karışma riski sıfır.' },
  { icon: 'ri-speed-up-line', title: 'En Hızlı Süre', desc: "Ara durak olmadan doğrudan teslimat yapılır; Türkiye'den Almanya'ya 3–5, diğer Avrupa ülkelerine 4–8 günde." },
  { icon: 'ri-file-list-3-line', title: 'Özel Programlama', desc: 'Yükleme ve boşaltma saatlerini sizinle birlikte planlıyoruz, tam esneklik sağlıyoruz.' },
  { icon: 'ri-shield-check-line', title: 'Sigortalı Taşıma', desc: 'CMR sigortası dahil, hasara karşı kapsamlı güvence sunuyoruz.' },
];

const faqs = [
  { q: 'Komple araç için minimum yük var mı?', a: 'Hayır, ancak ekonomik açıdan komple araç genellikle 5 ton veya 15 m³ üzeri yükler için tercih edilir.' },
  { q: 'Hangi araç tipleri mevcut?', a: 'Perdeli tır, tenteli tır, kapalı kasa, frigorifik araç ve lowbed gibi çeşitli araç tipleri sunuyoruz.' },
  { q: 'Teslimat süresi ne kadardır?', a: "Türkiye'den Almanya'ya doğrudan seferde ortalama 3–5 iş günü içinde teslimat yapılır." },
  { q: 'Özel yük (tehlikeli madde vb.) taşıyor musunuz?', a: 'Bazı özel yük kategorileri için ön izin gerekmektedir. Detaylar için bizimle iletişime geçin.' },
];

export default function KompleAracPage() {
  return (
    <main className="font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-brand-navy overflow-hidden">
        <div className="absolute inset-0">
          <ProgressiveImage
            src="/media/search-image-32a3a2866a.jpg"
            alt="Komple Araç Taşımacılık"
            className="object-cover object-top opacity-20"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/90 to-brand-navy/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Link to="/" className="text-white/50 text-sm hover:text-white transition-colors cursor-pointer">Anasayfa</Link>
            <span className="text-white/30 text-sm">/</span>
            <Link to="/hizmetlerimiz" className="text-white/50 text-sm hover:text-white transition-colors cursor-pointer">Hizmetlerimiz</Link>
            <span className="text-white/30 text-sm">/</span>
            <span className="text-brand-orange text-sm font-medium">Komple Araç</span>
          </div>
          <span className="inline-flex items-center gap-2 bg-brand-orange/20 border border-brand-orange/30 text-brand-orange text-xs font-semibold px-3 py-1 rounded-full mb-5 uppercase tracking-widest">
            <i className="ri-truck-line"></i> Hizmet 02
          </span>
          <h1 className="text-white font-heading font-bold text-4xl md:text-5xl mb-6 leading-tight">
            Komple Araç<br />
            <span className="text-brand-orange">Türkiye&apos;den Avrupa&apos;nın Her Ülkesine</span>
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            Büyük hacimli yükleriniz için komple araç tahsis ediyoruz. Tüm araç sizin yükünüze ayrılır, Türkiye&apos;nin 81 ilinden Avrupa&apos;nın 27 ülkesine doğrudan ve en kısa sürede teslimat yapılır.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <a
              href="https://wa.me/905322376576?text=Merhaba%2C%20komple%20ara%C3%A7%20ta%C5%9F%C4%B1ma%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-whatsapp hover:bg-whatsapp-dark text-white px-6 py-3 rounded-md text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-whatsapp-line text-base"></i></span>
              Fiyat Teklifi Al
            </a>
            <Link
              to="/iletisim"
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 hover:bg-white/20 text-white px-6 py-3 rounded-md text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-send-line text-base"></i></span>
              Form ile Ulaşın
            </Link>
          </div>
        </div>
      </section>

      {/* Visual + Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-3">Avantajlar</p>
              <h2 className="text-brand-navy font-heading font-bold text-3xl md:text-4xl mb-8">Komple Araç Neden Tercih Edilir?</h2>
              <div className="flex flex-col gap-5">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:border-brand-orange/20 transition-all duration-300">
                    <div className="w-11 h-11 flex items-center justify-center bg-brand-orange/10 text-brand-orange rounded-xl flex-shrink-0">
                      <i className={`${f.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-brand-navy font-heading font-bold text-sm mb-1">{f.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-[380px] md:h-[480px]">
              <ProgressiveImage
                src="/media/search-image-0c3aff752d.jpg"
                alt="Komple Araç Taşımacılık"
                className="object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-orange/10 text-brand-orange rounded-lg flex-shrink-0">
                    <i className="ri-truck-line text-xl"></i>
                  </div>
                  <div>
                    <div className="text-brand-navy font-heading font-bold text-sm">Tüm Araç Sizin İçin</div>
                    <div className="text-gray-400 text-xs mt-0.5">Başka yükle karışma riski sıfır</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Araç Tipleri */}
      <section className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-2xl overflow-hidden h-[340px] md:h-[420px]">
              <ProgressiveImage
                src="/media/search-image-548282b39a.jpg"
                alt="Araç Filosu"
                className="object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent pointer-events-none"></div>
            </div>
            <div>
              <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-3">Araç Filosu</p>
              <h2 className="text-brand-navy font-heading font-bold text-3xl md:text-4xl mb-8">Araç Tiplerimiz</h2>
              <div className="flex flex-col gap-4">
                {[
                  { icon: 'ri-truck-line', title: 'Perdeli / Tenteli Tır', cap: '24 ton / 90 m³', desc: 'Standart kargo ve palet yükler için en tercih edilen araç tipi.' },
                  { icon: 'ri-building-line', title: 'Kapalı Kasa', cap: '5–10 ton', desc: 'Küçük ve orta hacimli komple yükler için ideal kapalı araç.' },
                  { icon: 'ri-temp-cold-line', title: 'Frigorifik', cap: '24 ton / -25°C', desc: 'Soğuk zincir gerektiren gıda ve ilaç taşımacılığı için.' },
                ].map((v, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-white hover:border-brand-orange/20 transition-all duration-300">
                    <div className="w-11 h-11 flex items-center justify-center bg-brand-navy/5 text-brand-navy rounded-xl flex-shrink-0">
                      <i className={`${v.icon} text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-brand-navy font-heading font-bold text-sm">{v.title}</h3>
                      <p className="text-gray-400 text-xs mt-0.5">{v.desc}</p>
                    </div>
                    <span className="text-brand-orange text-xs font-bold whitespace-nowrap">{v.cap}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-12 text-center">
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-3">SSS</p>
            <h2 className="text-brand-navy font-heading font-bold text-3xl md:text-4xl">Sık Sorulan Sorular</h2>
            <p className="text-gray-400 text-sm mt-3">Merak ettiklerinizi tıklayarak öğrenin</p>
          </div>
          <Accordion items={faqs} />
        </div>
      </section>

      {/* Other Services */}
      <section className="py-16 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <p className="text-gray-400 text-sm mb-6 font-medium">Diğer Hizmetlerimiz</p>
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'Parsiyel Taşımacılık', href: '/hizmetlerimiz/parsiyel', icon: 'ri-box-3-line' },
              { label: 'Ev & Ofis Taşıma', href: '/hizmetlerimiz/ev-ofis-tasima', icon: 'ri-home-4-line' },
              { label: 'Hızlı Kargo', href: '/hizmetlerimiz/hizli-kargo', icon: 'ri-flashlight-line' },
            ].map((s) => (
              <Link
                key={s.href}
                to={s.href}
                className="inline-flex items-center gap-2 border border-gray-200 text-brand-navy hover:border-brand-orange hover:text-brand-orange px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className={`${s.icon} text-base`}></i></span>
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </main>
  );
}
